import { WHITELABEL_SETTINGS } from './config';
import { has, isEmpty, difference, reduce, indexOf } from 'lodash';

const ALL_GROUPS_ORDER = [
  'arrayOpGroup',
  'aiGroup',
  'automotiveGroup',
  'conditionalsGroup',
  'dtGroup',
  'financeGroup',
  'locationGroup',
  'lookupGroup',
  'mathGroup',
  'miscGroup',
  'securityGroup',
  'stringOpGroup',
  'uaGroup',
];

const ALL_GROUPS_RULES = {
  securityGroup: {
    label: 'Security',
    optionsOrder: ['md5', 'sha256'],
  },
  mathGroup: {
    label: 'Math',
    optionsOrder: [
      'abs',
      'add',
      'bucket',
      'toInteger',
      'toLong',
      'div',
      'toDouble',

      'multiply',
      'diff',
      'precision',
    ],
  },
  conditionalsGroup: {
    label: 'Conditional Logic',
    optionsOrder: [
      'and?',
      'ifElse',
      'empty?',
      'equal?',
      'greaterThan?',
      'lessThan?',
      'not?',
      'or?',
    ],
  },
  stringOpGroup: {
    label: 'String Operations',
    optionsOrder: [
      'regexMatch',
      'concatAttrs',
      'concatMultiple',
      'toJsonString',
      'toString',
      'substring',
      'regexFind',
      'toLower',
      'grok',
      'replaceAll',
      'split',
      'strlen',
      'trim',
      'toUpper',
    ],
  },
  arrayOpGroup: {
    label: 'Array Operations',
    optionsOrder: [
      'array_size',
      'concatArr',
      'join',
      'elementAt',
      'firstElement',
      'lastElement',
      'array_sort',
      'avg',
      'max',
      'min',
    ],
  },
  locationGroup: {
    label: 'Location',
    optionsOrder: [
      'dma',
      'postal',
      'city',
      'continent',
      'country',
      'country_code',
      'latitude',
      'longitude',
      'region_code',
      'region',
      'timezone',
    ],
  },
  lookupGroup: {
    label: 'Lookups',
    disabled: {
      flow_type: ['spark'],
    },
    optionsOrder: ['lookup', 'lookupList'],
  },
  financeGroup: {
    label: 'Finance',
    optionsOrder: ['USDConversionString'],
  },
  aiGroup: {
    label: 'Artificial Intelligence',
    optionsOrder: ['oneHotEncoding'],
  },
  uaGroup: {
    label: 'User Agent',
    optionsOrder: ['browser', 'browser_version', 'device_make', 'device_type', 'operating_system'],
  },
  dtGroup: {
    label: 'Date & Time',
    optionsOrder: [
      'convertTimeZone',
      'extractDate',
      'iso8601FromEpoch',
      'iso8601FromString',
      'timestampDifference',
    ],
  },
  automotiveGroup: {
    label: 'Automotive',
    optionsOrder: ['vin2country', 'vin2manufacturer', 'vin2year'],
  },
  miscGroup: {
    label: 'Miscellaneous',
    disabled: {
      options: {
        getNexsetData: {
          flow_type: ['spark', 'in_memory'],
        },
      },
    },
    optionsOrder: ['constVal', 'eqAttr', 'remove', 'hoist', 'getNexsetData'],
  },
};

const allowedGroupOrder = () => {
  const hiddenGroups = has(WHITELABEL_SETTINGS, 'tx.hideGrp') ? WHITELABEL_SETTINGS.tx.hideGrp : [];
  const allowedOrder = !isEmpty(hiddenGroups)
    ? difference(ALL_GROUPS_ORDER, hiddenGroups)
    : ALL_GROUPS_ORDER;

  return allowedOrder;
};

const allowedGroupRules = () => {
  const hiddenGroups = has(WHITELABEL_SETTINGS, 'tx.hideGrp') ? WHITELABEL_SETTINGS.tx.hideGrp : [];

  const allowedGroups = !isEmpty(hiddenGroups)
    ? reduce(
        ALL_GROUPS_RULES,
        (acc, currVal, currKey) => {
          if (indexOf(hiddenGroups, currKey) === -1) {
            acc[currKey] = currVal;
          }
          return acc;
        },
        {},
      )
    : ALL_GROUPS_RULES;

  return allowedGroups;
};

export const GROUPED_TRANSFORM_RULES = {
  groupOrder: allowedGroupOrder(),
  groupRules: allowedGroupRules(),
  rules: {
    md5: {
      value: 'md5',
      label: 'HashMD5',
      group: 'securityGroup',
      help: {
        desc: 'Returns MD5 encrypted value of the input. Use this transform for automatically encrypting any string value, for ex encrypting PII attributes.',
        ex: 'HashMD5("test") ➠ "098f6bcd4621d373cade4e832627b4f6"',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'Input To Hash',
        desc: 'The input string or attribute of which to return the encrypted value.',
      },
    },
    sha256: {
      value: 'sha256',
      label: 'HashSHA256',
      group: 'securityGroup',
      help: {
        desc: 'Returns SHA256 encrypted value of the input. Use this transform for automatically encrypting any string value, for ex encrypting PII attributes.',
        ex: 'HashSHA256("test") ➠ "9F86D081884C7D659A2FEAA0C55AD015A3BF4F1B2B0B822CD15D6C15B0F00A08"',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'Input To Hash',
        desc: 'The input string or attribute of which to return the encrypted value.',
      },
    },
    abs: {
      value: 'abs',
      label: 'Absolute',
      group: 'mathGroup',
      help: {
        desc: 'Returns the absolute value of a number. Interprets string as a number.',
        ex: 'Absolute(-22) ➠ 22',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'Input Number',
        desc: 'The number of which to return the absolute value.',
      },
    },
    add: {
      value: 'add',
      label: 'Add',
      group: 'mathGroup',
      help: {
        desc: 'Returns the sum of two numbers. This is equivalent to the applying  the `+` operator. Interprets string as a number.',
        ex: 'Add(2,3) ➠ 5',
      },
      requiredParams: ['__srcAttr', 'add_with'],
      __srcAttr: {
        display_name: 'Operand',
        desc: 'The first addend',
      },
      params: [
        {
          name: 'add_with',
          display_name: 'Number to Add',
          desc: 'The second addend.',
          typesAllowed: ['function', 'attributeRef', 'systemAttributeRef', 'number'],
          inputElement: 'text',
          format: 'number',
          default: {
            number: 0,
          },
        },
      ],
    },
    diff: {
      value: 'diff',
      label: 'Subtract',
      group: 'mathGroup',
      help: {
        desc: 'Returns the difference of two numbers. This is equivalent to applying the `-` operator. Interprets string as a number.',
        ex: 'Subtract(3,2) ➠ 1',
      },
      requiredParams: ['__srcAttr', 'to_subtract'],
      __srcAttr: {
        display_name: 'Operand 1',
        desc: 'The minuend, or number to be subtracted from.',
      },
      params: [
        {
          name: 'to_subtract',
          display_name: 'Operand 2',
          desc: 'The subtrahend, or number to subtract from.',
          typesAllowed: ['function', 'attributeRef', 'systemAttributeRef', 'number'],
          inputElement: 'text',
          format: 'number',
          default: {
            number: 0,
          },
        },
      ],
    },
    multiply: {
      value: 'multiply',
      label: 'Multiply',
      group: 'mathGroup',
      help: {
        desc: 'Returns the product of two numbers. This is equivalent to applying the `*` operator. Interprets string as a number.',
        ex: 'Multiply(3,2) ➠ 6',
      },
      requiredParams: ['__srcAttr', 'multiply_by'],
      __srcAttr: {
        display_name: 'Multiplicand 1',
        desc: 'The first mutiplicand to multiply',
      },
      params: [
        {
          name: 'multiply_by',
          display_name: 'Multiplicand 2',
          desc: 'The second multiplicand to multiply',
          typesAllowed: ['function', 'attributeRef', 'systemAttributeRef', 'number'],
          inputElement: 'text',
          format: 'number',
          default: {
            number: 1,
          },
        },
      ],
    },
    div: {
      value: 'div',
      label: 'Divide',
      group: 'mathGroup',
      help: {
        desc: 'Returns the result of dividing one number by another. Interprets string as a number.',
        ex: 'Divide(4,2) ➠ 2',
      },
      requiredParams: ['__srcAttr', 'div_by'],
      __srcAttr: {
        display_name: 'Dividend',
        desc: 'The number to be divided',
      },
      params: [
        {
          name: 'div_by',
          display_name: 'Divisor',
          desc: 'The number to divide by',
          typesAllowed: ['function', 'attributeRef', 'systemAttributeRef', 'number'],
          inputElement: 'text',
          format: 'number',
          default: {
            number: 1,
          },
        },
      ],
    },
    avg: {
      value: 'avg',
      label: 'Average',
      group: 'arrayGroup',
      help: {
        desc: 'Returns the average value of elements in an array. Note that this function does not average across records. Interprets string as a number.',
        ex: 'Average([2,4,6]) ➠ 8',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'Values Array',
        desc: 'The array attribute to extract the average of.',
      },
    },
    max: {
      value: 'max',
      label: 'Maximum',
      group: 'arrayGroup',
      help: {
        desc: 'Returns the maximum value of elements in an array. Note that this function does not compute maximum across records. Interprets string as a number.',
        ex: 'Maximum([2,4,6]) ➠ 6',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'Maximum Vale Of',
        desc: 'The array attribute to extract the maxium value from.',
      },
    },
    min: {
      value: 'min',
      label: 'Minimum',
      group: 'arrayGroup',
      help: {
        desc: 'Returns the minimum value of elements in an array. Note that this function does not compute minimum across records.',
        ex: 'Minimum([2,4,6]) ➠ 2',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'Values Array',
        desc: 'The array attribute to extract the minimum value from.',
      },
    },
    bucket: {
      value: 'bucket',
      label: 'Bucket',
      group: 'mathGroup',
      help: {
        desc: 'Segment data values into equal sized bins. Given a number, returns the integer bin into which the number would fall. Similarly, for an array of numbers, returns an array of bins where each element of array would fall. Interprets string as a number.',
        ex: 'Bucket(5, [11, 21, 99]) ➠ [10, 20, 95 ]',
      },
      requiredParams: ['__srcAttr', 'bucket_size'],
      __srcAttr: {
        display_name: 'Values Array',
        desc: 'The number or array of numbers that needs to be segment into bins. The bin/segment value is equal to the lowest number representing start of the bin.',
      },
      params: [
        {
          name: 'bucket_size',
          display_name: 'Bucket Size',
          desc: 'Size of  bucket or segment. The default bucket size is 10.',
          typesAllowed: ['number'],
          inputElement: 'text',
          format: 'number',
          default: {
            number: 1,
          },
        },
      ],
    },
    toDouble: {
      value: 'toDouble',
      label: 'ConvertToDouble',
      group: 'mathGroup',
      help: {
        desc: 'Returns a floating point (double precision) representation of a number. Interprets string as a number.',
        ex: 'ConvertToDouble("2.65") ➠ 2.65',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'Value',
        desc: 'The number or string that needs to be converted to a double precision number. The returned value does not contain trailing zeros. Ex:Convert_To_Double(2) ➠ 2. Note that the function has a 32bit max signed integer limit. ',
      },
    },
    toInteger: {
      value: 'toInteger',
      label: 'ConvertToInteger',
      group: 'mathGroup',
      help: {
        desc: 'Rounds a number down to its nearest integer value. Interprets string as number. ',
        ex: 'ConvertToInteger("2.65") ➠ 2',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'Value',
        desc: 'The number or string that needs to be converted to an integer.  Note that the function has a 32bit max signed integer limit.',
      },
    },
    toLong: {
      value: 'toLong',
      label: 'ConvertToLong',
      group: 'mathGroup',
      help: {
        desc: 'Rounds a number down to its nearest 64bit integer value. Interprets string as number.',
        ex: 'ConvertToLong("2.65") ➠ 2',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'Value',
        desc: 'The number or string that needs to be converted to an integer. Note that the function has a 64bit max signed integer limit.',
      },
    },
    precision: {
      value: 'precision',
      label: 'ToPrecision',
      group: 'mathGroup',
      help: {
        desc: 'Returns a floating point representation of a number by converting to specified precision. Interprets string as a number.',
        ex: 'ToPrecision(2, 2.44444) ➠ 2.44',
      },
      requiredParams: ['__srcAttr', 'scale'],
      __srcAttr: {
        display_name: 'Value',
        desc: 'The number or string that needs to be converted. The returned value does not contain trailing zeros. Ex: To_Precision(2, 2.40444) ➠ 2.4. Note that the function has a 32bit max signed integer limit.',
      },
      params: [
        {
          name: 'scale',
          display_name: 'Scale',
          desc: 'The precision or significance that the number should be converted to.',
          typesAllowed: ['number'],
          inputElement: 'text',
          format: 'number',
          default: {
            number: 0,
          },
        },
      ],
    },
    'not?': {
      value: 'not?',
      label: 'IsNotTrue',
      group: 'conditionalsGroup',
      help: {
        desc: 'Returns boolean true if input is false, blank string, or empty array.',
        ex: 'IsNotTrue(false)  ➠ true',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'Apply on',
        desc: 'The input expression that this logical operation needs to be applied on.',
      },
    },
    'empty?': {
      value: 'empty?',
      label: 'IsEmpty',
      group: 'conditionalsGroup',
      help: {
        desc: 'Returns boolean true if input is null, blank string, or empty array',
        ex: 'IsEmpty("")  ➠ true',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'Apply on',
        desc: 'The input expression that this logical operation needs to be applied on.',
      },
    },
    'equal?': {
      value: 'equal?',
      label: 'IsEqualTo',
      group: 'conditionalsGroup',
      help: {
        desc: 'Returns boolean true if both input expressions or values are equal.',
        ex: 'IsEqual(1,1)  ➠ true',
      },
      requiredParams: ['__srcAttr', 'withAttr'],
      __srcAttr: {
        display_name: 'First Value',
        desc: 'The first expression or value that needs to be compared with the second value.',
      },
      params: [
        {
          name: 'withAttr',
          display_name: 'Equal To',
          desc: 'The second expression or value to test first value against.',
          typesAllowed: ['function', 'attributeRef', 'systemAttributeRef', 'string', 'number'],
          inputElement: 'text',
          format: 'string',
          default: {
            string: '',
            number: 0,
          },
        },
      ],
    },
    'lessThan?': {
      value: 'lessThan?',
      label: 'IsLessThan',
      group: 'conditionalsGroup',
      help: {
        desc: 'Returns boolean true if first value is less than the second value',
        ex: 'IsLessThan(1,2)  ➠ true',
      },
      requiredParams: ['__srcAttr', 'withAttr'],
      __srcAttr: {
        display_name: 'First Value',
        desc: 'The first expression or value that needs to be compared with the second value.',
      },
      params: [
        {
          name: 'withAttr',
          display_name: 'Less Than',
          desc: 'The second expression or value to test first value against.',
          typesAllowed: ['function', 'attributeRef', 'systemAttributeRef', 'string', 'number'],
          inputElement: 'text',
          format: 'string',
          default: {
            string: '',
            number: 0,
          },
        },
      ],
    },
    'greaterThan?': {
      value: 'greaterThan?',
      label: 'IsGreaterThan',
      group: 'conditionalsGroup',
      help: {
        desc: 'Returns boolean true if first value is greater than the second value',
        ex: 'IsGreaterThan(2,1)  ➠ true',
      },
      requiredParams: ['__srcAttr', 'withAttr'],
      __srcAttr: {
        display_name: 'First Value',
        desc: 'The first expression or value that needs to be compared with the second value.',
      },
      params: [
        {
          name: 'withAttr',
          display_name: 'Greater Than',
          desc: 'The second expression or value to test first value against.',
          typesAllowed: ['function', 'attributeRef', 'systemAttributeRef', 'string', 'number'],
          inputElement: 'text',
          format: 'string',
          default: {
            string: '',
            number: 0,
          },
        },
      ],
    },
    'or?': {
      value: 'or?',
      label: 'Or',
      group: 'conditionalsGroup',
      help: {
        desc: 'Returns boolean true if either input evaluates to true',
        ex: 'Or(true,false)  ➠ true',
      },
      requiredParams: ['__srcAttr', 'withAttr'],
      __srcAttr: {
        display_name: 'First Value',
        desc: 'The first expression that this logical operation needs to be applied on.',
      },
      params: [
        {
          name: 'withAttr',
          display_name: 'Second value',
          desc: 'The second expression this logical operation needs to be applied on.',
          typesAllowed: ['function', 'attributeRef', 'systemAttributeRef'],
          inputElement: 'text',
          format: 'string',
        },
      ],
    },
    'and?': {
      value: 'and?',
      label: 'And',
      group: 'conditionalsGroup',
      help: {
        desc: 'Returns boolean true if both inputs evaluate to true',
        ex: 'And(true,false)  ➠ false',
      },
      requiredParams: ['__srcAttr', 'withAttr'],
      __srcAttr: {
        display_name: 'First value',
        desc: 'The first expression that this logical operation needs to be applied on.',
      },
      params: [
        {
          name: 'withAttr',
          display_name: 'Second Value',
          desc: 'The second expression this logical operation needs to be applied on.',
          typesAllowed: ['function', 'attributeRef', 'systemAttributeRef'],
          inputElement: 'text',
          format: 'string',
        },
      ],
    },
    ifElse: {
      value: 'ifElse',
      label: 'IfElse',
      group: 'conditionalsGroup',
      help: {
        desc: 'Return values based on truthiness of a logical expression. Returns one value if the expression evaluates to true and another value if it evaluates to false.',
        ex: 'IfElse(IsEmpty("ab"),1,2)   ➠ 2 ',
      },
      requiredParams: ['__srcAttr', 'truthyAttr', 'falsyAttr'],
      __srcAttr: {
        display_name: 'Condition',
        desc: 'The logical expression that needs to be evaluated.',
        allowedRules: {
          conditionalsGroup: {
            optionsOrder: ['not?', 'empty?', 'equal?', 'lessThan?', 'greaterThan?', 'or?', 'and?'],
          },
        },
      },
      params: [
        {
          name: 'truthyAttr',
          display_name: 'Value if true',
          desc: 'Value to be returned if expression evaluates to true.',
          typesAllowed: ['function', 'attributeRef', 'systemAttributeRef', 'string', 'number'],
          inputElement: 'text',
          format: 'string',
          default: {
            string: '',
            number: 0,
          },
        },
        {
          name: 'falsyAttr',
          display_name: 'Value if false',
          desc: 'Value to be returned if expression evaluates to false.',
          typesAllowed: ['function', 'attributeRef', 'systemAttributeRef', 'string', 'number'],
          inputElement: 'text',
          format: 'string',
          default: {
            string: '',
            number: 0,
          },
        },
      ],
    },
    concatAttrs: {
      value: 'concatAttrs',
      label: 'ConcatenateTwoAttributes',
      group: 'stringOpGroup',
      help: {
        desc: 'Concatenate two attributes or function outputs into a single attribute. Note that this function is different from the Join function for arrays which joins elements of a string array.',
        ex: 'ConcatenateTwoAttributes(["First","and","Second"])  ➠  "FirstandSecond"',
      },
      requiredParams: ['__srcAttr', 'separator', 'joinWithAttr'],
      __srcAttr: {
        display_name: 'First Item',
        desc: 'The first attribute or function output that needs to be joined. This value appears in output before the delimiter.',
      },
      params: [
        {
          name: 'separator',
          display_name: 'Delimiter',
          desc: 'The character or string to place between each concatenated attribute',
          typesAllowed: ['string'],
          inputElement: 'text',
          format: 'string',
          default: {
            string: '',
          },
        },
        {
          name: 'joinWithAttr',
          display_name: 'Second Item',
          desc: 'The second attribute or function output that needs to be joined. This value appears in output after the delimiter.',
          typesAllowed: ['function', 'attributeRef', 'systemAttributeRef', 'string'],
          inputElement: 'text',
          format: 'string',
          default: {
            string: '',
          },
        },
      ],
    },
    concatMultiple: {
      value: 'concatMultiple',
      label: 'ConcatenateMultipleAttributes',
      group: 'stringOpGroup',
      help: {
        desc: 'Concatenate any number of attributes or function outputs into a single attribute. Note that this function is different from the Join function for arrays which joins elements of a string array.',
        ex: 'ConcatenateMultipleAttributes(["First","and","Second"])  ➠  "FirstandSecond"',
      },
      requiredParams: ['elements'],
      ignoreSrc: true,
      params: [
        {
          name: 'elements',
          display_name: 'Values array',
          desc: 'The attributes or function outputs that need to be joined.',
          typesAllowed: ['attributeRef', 'string', 'function', 'systemAttributeRef'],
          inputElement: 'array',
          format: 'string',
          default: {
            string: '',
          },
        },
      ],
    },
    split: {
      value: 'split',
      label: 'Split',
      group: 'stringOpGroup',
      help: {
        desc: 'Split a string into an array of strings based on a specified separator. The separator is not included in the returned String array. Adjacent separators are treated as one separator. ',
        ex: 'Split("This and that and more"," ") ➠  ["This","and","that","and","more"]',
      },
      requiredParams: ['__srcAttr', 'delimiter'],
      __srcAttr: {
        display_name: 'String to split',
        desc: 'The text or string to split into arrays.',
      },
      params: [
        {
          name: 'delimiter',
          display_name: 'Separator',
          desc: 'The character or characters used as delimiters. Null or empty delimiter splits on whitespace. This function does not split on string pattern. Instead a string separator will split on each character of separator.',
          typesAllowed: ['string'],
          inputElement: 'text',
          format: 'string',
          default: {
            string: '',
          },
        },
      ],
    },
    toUpper: {
      value: 'toUpper',
      label: 'UppercaseString',
      group: 'stringOpGroup',
      help: {
        desc: 'Converts all of the characters in this string to upper case.',
        ex: 'UppercaseString("hello")  ➠  "HELLO"',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'String',
        desc: 'The text or string that should be converted to uppercase',
      },
    },
    toLower: {
      value: 'toLower',
      label: 'LowercaseString',
      group: 'stringOpGroup',
      help: {
        desc: 'Converts all of the characters in this string to lower case.',
        ex: 'LowercaseString("HELLO")  ➠  "hello"',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'String',
        desc: 'The text or string that should be converted to lowercase',
      },
    },
    toString: {
      value: 'toString',
      label: 'ConvertToString',
      group: 'stringOpGroup',
      help: {
        desc: "Returns String representation of the input attribute. If the input is an array, this returns a string representation of the contents of the specified array, i.e. a list of the array's elements, enclosed in square brackets ([ ])",
        ex: 'ConvertToString(123) ➠ "123" \nConvertToString(["This", 1, "that"]) ➠ ["This", "1","that"]',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'Value',
        desc: 'The input attribute that needs to be converted. Note that the returned type will be an array of strings if the input was an array.',
      },
    },
    replaceAll: {
      value: 'replaceAll',
      label: 'ReplaceSubstring',
      group: 'stringOpGroup',
      help: {
        desc: 'Replaces each substring of this string that matches the given substring pattern with the given replacement.',
        ex: 'ReplaceSubstring("This and that", "and","or") ➠ "This or that"',
      },
      requiredParams: ['__srcAttr', 'fromStr', 'toStr'],
      __srcAttr: {
        display_name: 'Apply on',
        desc: 'The input text or string that should be parsed.',
      },
      params: [
        {
          name: 'fromStr',
          display_name: 'Pattern to Replace',
          desc: 'The substring or pattern which needs to be replaced',
          typesAllowed: ['string'],
          inputElement: 'text',
          format: 'string',
          default: {
            string: '',
          },
        },
        {
          name: 'toStr',
          display_name: 'Replacement String',
          desc: 'The string to be substituted for each match',
          typesAllowed: ['string'],
          inputElement: 'text',
          format: 'string',
          default: {
            string: '',
          },
        },
      ],
    },
    substring: {
      value: 'substring',
      label: 'ExtractSubstring',
      group: 'stringOpGroup',
      help: {
        desc: 'Returns a string that is a substring of the input string. The substring begins at the specified start and extends to the character before the end position. Position counting begins with index 0. Leave the end position to blank to extract till end of string. Use negative numbers for counting position from end.',
        ex: 'ExtractSubstring("0123", 0, 1) ➠ "0" \nExtractSubstring("0123", -1) ➠ 3',
      },
      requiredParams: ['__srcAttr', 'fromPos', 'toPos'],
      __srcAttr: {
        display_name: 'Extract from',
        desc: 'The string to extract substrings from.',
      },
      params: [
        {
          name: 'fromPos',
          display_name: 'From position',
          desc: 'Index of the first character that should be included in the new substring. Index counting begins from 0. Use negative numbers to count backwards from the end of the string, i.e. -1 to specify last character.',
          typesAllowed: ['number'],
          inputElement: 'text',
          format: 'number',
          default: {
            number: 0,
          },
        },
        {
          name: 'toPos',
          display_name: 'To position',
          desc: 'The position where substring extraction should end. Position counting begins at 0. This is exclusive, so value 1 will stop extraction before second character.',
          typesAllowed: ['number'],
          inputElement: 'text',
          format: 'number',
          default: {
            number: '',
          },
        },
      ],
    },
    strlen: {
      value: 'strlen',
      label: 'StringLength',
      group: 'stringOpGroup',
      help: {
        desc: 'Returns the length of this string. The length is equal to the number of Unicode code units in the string.',
        ex: 'StringLength("ABCD") ➠ 4',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'Length of',
        desc: 'The input string to be processed',
      },
    },
    trim: {
      value: 'trim',
      label: 'Trim',
      group: 'stringOpGroup',
      help: {
        desc: 'Returns a string whose value is this string with any leading and trailing whitespace removed.',
        ex: 'Trim(" ABCD ") ➠ "ABCD"',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'String to trim',
        desc: 'The input string to be processed',
      },
    },
    toJsonString: {
      value: 'toJsonString',
      label: 'ToJSONString',
      group: 'stringOpGroup',
      help: {
        desc: 'Converts a JSON Object/Array to a JSON String. This is useful for writing nested objects to database columns or lookup tables. Note that if the input is already a string it will be written as is.',
        ex: 'ConvertJSONObjecttoJSONString({"one": 1, "two":2 }) ➠ "{\\"one\\":1,\\"two\\":2}"',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'Apply on',
        desc: 'The input object/attribute that needs to be flattened into a JSON string. This is best suited for cases where input attribute is a valid nested object or array of objects.',
      },
    },
    grok: {
      value: 'grok',
      label: 'ParseGrok',
      group: 'stringOpGroup',
      help: {
        desc: 'Parse unstructured log data into an object of structured fields using a grok pattern. Returns an object with predefined keys for that log format if text can be parsed, and an empty object if the text cannot be parsed. Select a predefined pattern or create your own valid grok pattern depending on the log format.',
        ex: 'ParseLogLines(\'Jan  1 06:25:43 mailserver14 postfix/cleanup[21403]: BEF25A72965: message-id=<20130101142543.5828399CCAF@mailserver14.example.com>\', \'%SYSLOGBASE%\') ➠ \n{\n"MONTH": "Jan",\n"SYSLOGBASE": "Jan  1 06:25:43 mailserver14 postfix/cleanup[21403]:",\n"SYSLOGPROG": "postfix/cleanup[21403]",\n"HOUR": "06",\n"IPV6": null,\n"SYSLOGFACILITY": null,\n"IP": null,\n"IPV4": null,\n"TIME": "06:25:43",\n"pid": "21403",\n"program": "postfix/cleanup",\n"priority": null,\n"logsource": "mailserver14",\n"IPORHOST": "mailserver14",\n"HOSTNAME": "mailserver14",\n"MINUTE": "25",\n"SECOND": "43",\n"facility": null,\n"MONTHDAY": "1",\n"timestamp": "Jan  1 06:25:43"}',
      },
      requiredParams: ['__srcAttr', 'grokPattern'],
      __srcAttr: {
        display_name: 'Log string',
        desc: 'The input string that should be parsed. Usually this should be text streamed from a standard log file like server logs.',
        typesAllowed: ['attributeRef', 'systemAttributeRef'],
      },
      params: [
        {
          name: 'grokPattern',
          display_name: 'Grok Pattern',
          desc: 'Select a predefined pattern or create your own valid grok pattern\n\n            Predefined Grok Patterns:\n\n            %{SYSLOGBASE} - Grok pattern for syslog\n\n            %{COMBINEDAPACHELOG} - Grok pattern for apache log\n            %{NAGIOSLOGLINE} - Grok pattern for Nagios log\n            %{POSTGRESQL} - Grok pattern for postgres server log\n            %{REDISLOG} - Grok pattern for redis server log\n            %{JAVASTACKTRACEPART} - Grok pattern for java stack trace.\n            ',
          typesAllowed: ['string'],
          inputElement: 'select-creatable',
          values: [
            '%{SYSLOGBASE}',
            '%{COMBINEDAPACHELOG}',
            '%{NAGIOSLOGLINE}',
            '%{POSTGRESQL}',
            '%{REDISLOG}',
            '%{JAVASTACKTRACEPART}',
          ],
        },
      ],
    },
    regexFind: {
      value: 'regexFind',
      label: 'FindPattern',
      group: 'stringOpGroup',
      help: {
        desc: 'Returns an array of all matches of the regular expression in the input string.',
        ex: 'FindPattern("abcd1234e5",[a-zA-Z][1-9]) ➠ ["d1","e5"]',
      },
      requiredParams: ['__srcAttr', 'regexPattern'],
      __srcAttr: {
        display_name: 'Input string',
        desc: 'Input string or text to extract substrings from.',
      },
      params: [
        {
          name: 'regexPattern',
          display_name: 'Pattern',
          desc: 'Regular expression pattern to test the input string against.',
          typesAllowed: ['string'],
          inputElement: 'text',
          format: 'string',
          default: {
            string: '.*',
          },
        },
      ],
    },
    regexMatch: {
      value: 'regexMatch',
      label: 'TestPattern',
      group: 'stringOpGroup',
      help: {
        desc: 'Returns boolean true or false depending on whether the input string matches provided regular expression or not.',
        ex: 'Test Pattern("test@acme.com",^([a-zA-Z0-9-.]+)@([a-zA-Z0-9-.]+).([a-zA-Z]{2,5})$) ➠ true',
      },
      requiredParams: ['__srcAttr', 'regexPattern'],
      __srcAttr: {
        display_name: 'Input string',
        desc: 'Input string or text to test against the regular expression pattern.',
      },
      params: [
        {
          name: 'regexPattern',
          display_name: 'Pattern',
          desc: 'Regular expression pattern to test the input string against.',
          typesAllowed: ['string'],
          inputElement: 'text',
          format: 'string',
          default: {
            string: '.*',
          },
        },
      ],
    },
    join: {
      value: 'join',
      label: 'ConcatenateWithDelimiter',
      group: 'arrayOpGroup',
      help: {
        desc: 'Join all elements of an array into a string. There is no separator applied between elements in this functions.',
        ex: 'ConcatenateWithDelimiter(["First","Second"], " and ")  ➠  "First and Second"',
      },
      requiredParams: ['__srcAttr', 'delimiter'],
      __srcAttr: {
        display_name: 'Values array',
        desc: 'Input array that needs to be converted to a string',
        typesAllowed: ['attributeRef', 'systemAttributeRef', 'function', 'array'],
      },
      params: [
        {
          name: 'delimiter',
          display_name: 'Separator',
          desc: 'The character or string separtor for joining array elements.',
          typesAllowed: ['string'],
          inputElement: 'text',
          format: 'string',
          default: {
            string: '',
          },
        },
      ],
    },
    concatArr: {
      value: 'concatArr',
      label: 'Concatenate',
      group: 'arrayOpGroup',
      help: {
        desc: 'Joins all elements of an array into a string.',
        ex: 'Concatenate(["First","Second"]) ➠ "FirstSecond"',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'Values array',
        desc: 'Input array that needs to be converted to a string',
      },
    },
    array_sort: {
      value: 'array_sort',
      label: 'SortArray',
      group: 'arrayOpGroup',
      help: {
        desc: 'Sorts the specified array of objects into ascending order. All elements in the array must be mutually comparable.',
        ex: 'Sort([4,3,1,5]) ➠ [1,3,4,5]',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'Array to sort',
        desc: 'The array to be sorted.',
      },
    },
    array_size: {
      value: 'array_size',
      label: 'ArraySize',
      group: 'arrayOpGroup',
      help: {
        desc: 'Returns the number of elements in an array. Though not recommended for usage with other data types, it will length of a string  if applied to a string, or number of key-value pairs if applied to an object.',
        ex: 'ArraySize([4,3,1,5]) ➠ 4',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'Size of',
        desc: 'The input array to fetch count of.',
      },
    },
    firstElement: {
      value: 'firstElement',
      label: 'FirstElement',
      group: 'arrayOpGroup',
      help: {
        desc: 'Returns the first element of an array.',
        ex: 'FirstElement([4,3,1,5]) ➠ 4',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'Values array',
        desc: 'The input array to get first element of.',
      },
    },
    lastElement: {
      value: 'lastElement',
      label: 'LastElement',
      group: 'arrayOpGroup',
      help: {
        desc: 'Returns last element of an array',
        ex: 'LastElement([4,3,1,5]) ➠ 5',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'Values array',
        desc: 'The input array to get first element of.',
      },
    },
    elementAt: {
      value: 'elementAt',
      label: 'ElementAtIndex',
      group: 'arrayOpGroup',
      help: {
        desc: 'Return the element at specified index of an array. Index counting begins at 0.',
        ex: 'ElementatIndex([4,3,1,5], 2) ➠ 1',
      },
      requiredParams: ['__srcAttr', 'index'],
      __srcAttr: {
        display_name: 'Values array',
        desc: 'The input array to get element of.',
      },
      params: [
        {
          name: 'index',
          display_name: 'Index',
          desc: 'Array element to be extracted. The index counting starts at 0.',
          typesAllowed: ['number'],
          inputElement: 'text',
          format: 'string',
          default: {
            number: 0,
          },
        },
      ],
    },
    country: {
      value: 'country',
      label: 'IPToCountry',
      group: 'locationGroup',
      help: {
        desc: 'Identify country name from an IP Address.',
        ex: 'IPToCountry("73.223.52.74") ➠ "United States"',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'IP Address',
        desc: 'IP Address for extracting the geolocation information from.',
      },
    },
    country_code: {
      value: 'country_code',
      label: 'IPToCountryCode',
      group: 'locationGroup',
      help: {
        desc: 'Identify 2 letter country code from an IP Address',
        ex: 'IPToCountryCode("73.223.52.74") ➠ "US"',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'IP Address',
        desc: 'IP Address for extracting the geolocation information from.',
      },
    },
    city: {
      value: 'city',
      label: 'IPToCity',
      group: 'locationGroup',
      help: {
        desc: 'Identify city from IP address',
        ex: 'IPToCity("73.223.52.74") ➠ "San Mateo"',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'IP Address',
        desc: 'IP Address for extracting the geolocation information from.',
      },
    },
    postal: {
      value: 'postal',
      label: 'IPToZipCode',
      group: 'locationGroup',
      help: {
        desc: 'Identify zip code from IP address',
        ex: 'IPToZip("73.223.52.74") ➠ "94401"',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'IP Address',
        desc: 'IP Address for extracting the geolocation information from.',
      },
    },
    region: {
      value: 'region',
      label: 'IPToState',
      group: 'locationGroup',
      help: {
        desc: 'Identify state from IP address',
        ex: 'IPToState("73.223.52.74") ➠ "California"',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'IP Address',
        desc: 'IP Address for extracting the geolocation information from.',
      },
    },
    continent: {
      value: 'continent',
      label: 'IPToContinent',
      group: 'locationGroup',
      help: {
        desc: 'Identify continent from IP address',
        ex: 'IPToContinent("73.223.52.74") ➠ "North America"',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'IP Address',
        desc: 'IP Address for extracting the geolocation information from.',
      },
    },
    latitude: {
      value: 'latitude',
      label: 'IPToLatitude',
      group: 'locationGroup',
      help: {
        desc: 'Identify latitude from IP address',
        ex: 'IPToLatitude("73.223.52.74") ➠ "37.567"',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'IP Address',
        desc: 'IP Address for extracting the geolocation information from.',
      },
    },
    longitude: {
      value: 'longitude',
      label: 'IPToLongitude',
      group: 'locationGroup',
      help: {
        desc: 'Identify longitude from IP address',
        ex: 'IPToLongitude("73.223.52.74") ➠ "-122.3279"',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'IP Address',
        desc: 'IP Address for extracting the geolocation information from.',
      },
    },
    dma: {
      value: 'dma',
      label: 'IPToDMA',
      group: 'locationGroup',
      help: {
        desc: 'Identify DMA from IP address',
        ex: 'IPToDMA("73.223.52.74") ➠ 807',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'IP Address',
        desc: 'IP Address for extracting the geolocation information from.',
      },
    },
    timezone: {
      value: 'timezone',
      label: 'IPToTimezone',
      group: 'locationGroup',
      help: {
        desc: 'Identify timezone from IP address',
        ex: 'IPToTimezone("73.223.52.74") ➠ "America/LosAngeles"',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'IP Address',
        desc: 'IP Address for extracting the geolocation information from.',
      },
    },
    region_code: {
      value: 'region_code',
      label: 'IPToRegionCode',
      group: 'locationGroup',
      help: {
        desc: 'Identify region/state code from IP address',
        ex: 'IPToRegionCode("73.223.52.74") ➠ "CA"',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'IP Address',
        desc: 'IP Address for extracting the geolocation information from.',
      },
    },
    lookup: {
      value: 'lookup',
      label: 'Lookup',
      group: 'lookupGroup',
      help: {
        desc: 'Finds Lookup row  based on match of input attribute value with the primary key values of the specified Nexla lookup and returns the value of the Lookup Secondary key column. Secondary key is only required if the Lookup has more than one secondary key columns.',
        ex: 'If Lookup 1200 has a row with primary key value "1" and corresponding "code" value "One" \n\nLookup("1",1200,"code") ➠ "One"',
      },
      requiredParams: ['__srcAttr', 'data_map'],
      optionalParams: [
        {
          name: 'subKey',
          display_name: 'Secondary Key',
          desc: 'Secondary Key',
          typesAllowed: ['string'],
          inputElement: 'lookup-secondary-key',
        },
      ],
      __srcAttr: {
        display_name: 'Apply lookup on',
        desc: 'Input attribute to match against primary key values of the Nexla lookup.',
      },
      params: [
        {
          name: 'data_map',
          display_name: 'Nexla Lookup',
          desc: 'Nexla Static or Dynamic lookup to match against',
          typesAllowed: ['number'],
          inputElement: 'lookup',
          secondaryKeySelector: true,
          format: 'number',
        },
      ],
    },
    lookupList: {
      value: 'lookupList',
      label: 'GetLookupEntries',
      group: 'lookupGroup',
      help: {
        desc: 'Fetch entire object from a lookup table.  Lookup entries are filtered based on exact match of input with lookup primary key. Returned value is a JSON object of <key>:<value> pairs that match <column-name>:<row-value> for the matching row.',
        ex: 'GetLookupEntries(1200,"1") ➠ {"id":1,"code":"One", "codeLower":"one"}',
      },
      ignoreSrc: true,
      requiredParams: ['data_map', 'lookupKey'],
      params: [
        {
          name: 'data_map',
          display_name: 'Nexla Lookup',
          desc: 'Nexla Static or Dynamic lookup to fetch entries from.',
          typesAllowed: ['number'],
          inputElement: 'lookup-nosecondary',
          secondaryKeySelector: false,
          format: 'number',
        },
        {
          name: 'lookupKey',
          display_name: 'Match Criteria',
          desc: 'Input attribute for filtering lookup entries. The filter is applied based on exact match of primary key with this value.',
          typesAllowed: ['attributeRef', 'systemAttributeRef', 'function', 'string'],
          inputElement: 'text',
          default: {
            string: '',
          },
        },
      ],
    },
    USDConversionString: {
      value: 'USDConversionString',
      label: 'CurrencyConversionFromUSD',
      group: 'financeGroup',
      help: {
        desc: 'Performs currency conversion from USD to desired currency format by multiplying using latest exchange rates. Exchange rates are updated every 6 hours. Note that the input value must be a number without the currency sign.',
        ex: 'CurrencyConversion(100,"EUR") ➠ 84.6475',
      },
      requiredParams: ['__srcAttr', 'from_usd_to_cur'],
      __srcAttr: {
        display_name: 'Amount in USD',
        desc: 'Input USD number that needs to be converted based on recent currency exchange rates.',
      },
      params: [
        {
          name: 'from_usd_to_cur',
          display_name: 'Target Currency',
          desc: 'The currency to convert this value to.',
          typesAllowed: ['string'],
          inputElement: 'select',
          values: [
            'USD',
            'AED',
            'AFN',
            'ALL',
            'AMD',
            'ANG',
            'AOA',
            'ARS',
            'AUD',
            'AWG',
            'AZN',
            'BAM',
            'BBD',
            'BDT',
            'BGN',
            'BHD',
            'BIF',
            'BMD',
            'BND',
            'BOB',
            'BRL',
            'BSD',
            'BTN',
            'BWP',
            'BYN',
            'BYR',
            'BZD',
            'CAD',
            'CDF',
            'CHF',
            'CLF',
            'CLP',
            'CNH',
            'CNY',
            'COP',
            'COPPER HIGHGRADE',
            'CRC',
            'CUP',
            'CVE',
            'CYP',
            'CZK',
            'DEM',
            'DJF',
            'DKK',
            'DOP',
            'DZD',
            'ECS',
            'EGP',
            'ERN',
            'ETB',
            'EUR',
            'FJD',
            'FKP',
            'FRF',
            'GBP',
            'GEL',
            'GHS',
            'GIP',
            'GMD',
            'GNF',
            'GOLD 1 OZ',
            'GTQ',
            'GYD',
            'HKD',
            'HNL',
            'HRK',
            'HTG',
            'HUF',
            'IDR',
            'IEP',
            'ILS',
            'INR',
            'IQD',
            'IRR',
            'ISK',
            'ITL',
            'JMD',
            'JOD',
            'JPY',
            'KES',
            'KGS',
            'KHR',
            'KMF',
            'KPW',
            'KRW',
            'KWD',
            'KYD',
            'KZT',
            'LAK',
            'LBP',
            'LKR',
            'LRD',
            'LSL',
            'LTL',
            'LVL',
            'LYD',
            'MAD',
            'MDL',
            'MGA',
            'MKD',
            'MMK',
            'MNT',
            'MOP',
            'MRO',
            'MUR',
            'MVR',
            'MWK',
            'MXN',
            'MXV',
            'MYR',
            'MZN',
            'NAD',
            'NGN',
            'NIO',
            'NOK',
            'NPR',
            'NZD',
            'OMR',
            'PAB',
            'PALLADIUM 1 OZ',
            'PEN',
            'PGK',
            'PHP',
            'PKR',
            'PLATINUM 1 UZ 999',
            'PLN',
            'PYG',
            'QAR',
            'RON',
            'RSD',
            'RUB',
            'RWF',
            'SAR',
            'SBD',
            'SCR',
            'SDG',
            'SEK',
            'SGD',
            'SHP',
            'SILVER 1 OZ 999 NY',
            'SIT',
            'SLL',
            'SOS',
            'SRD',
            'STD',
            'SVC',
            'SYP',
            'SZL',
            'THB',
            'TJS',
            'TMT',
            'TND',
            'TOP',
            'TRY',
            'TTD',
            'TWD',
            'TZS',
            'UAH',
            'UGX',
            'UYU',
            'UZS',
            'VEF',
            'VND',
            'VUV',
            'WST',
            'XAF',
            'XCD',
            'XDR',
            'XOF',
            'XPF',
            'YER',
            'ZAR',
            'ZMW',
            'ZWL',
          ],
        },
      ],
    },
    oneHotEncoding: {
      value: 'oneHotEncoding',
      label: 'OneHotEncoding',
      group: 'aiGroup',
      help: {
        desc: 'One hot encode an attribute into an object. One hot encoding is a process by which categorical variables are converted into a form that could be provided to ML algorithms to do a better job in prediction. This transform takes a Nexla Lookup and an attribute as the input and returns an object. The returned object has the same keys as the Lookup, the key matching the input attribute has value of 1 and all other keys have value of 0',
        ex: 'OneHotEncoding(<attr>,1234) ➠  object with one key <attr> and value: 1, and all other columns from 1234 as keys with value 0.',
      },
      requiredParams: ['__srcAttr', 'data_map'],
      __srcAttr: {
        display_name: 'Attribute to Encode',
        desc: 'Attribute that if present as key in lookup will be encoded to 1',
      },
      params: [
        {
          name: 'data_map',
          display_name: 'Nexla Lookup',
          desc: 'Nexla Static or Dynamic lookup to perform encoding on.',
          typesAllowed: ['string'],
          inputElement: 'lookup-nosecondary',
          secondaryKeySelector: false,
          format: 'string',
        },
      ],
    },
    device_make: {
      value: 'device_make',
      label: 'UserAgentToDeviceMake',
      group: 'uaGroup',
      help: {
        desc: 'Extracts Device Make from a User Agent string',
        ex: 'UserAgenttoDeviceMake("Mozilla/5.0 (iPhone; CPU iPhone OS 1351 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/13.1.1 Mobile/15E148 Safari/604.1") ➠ "Apple Inc."',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'User Agent',
        desc: 'Input user agent to be parsed',
      },
    },
    device_type: {
      value: 'device_type',
      label: 'UserAgentToDeviceType',
      group: 'uaGroup',
      help: {
        desc: 'Extracts Device Type from User Agent',
        ex: 'UserAgenttoDeviceType("Mozilla/5.0 (iPhone; CPU iPhone OS 1351 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/13.1.1 Mobile/15E148 Safari/604.1") ➠ "Smartphone"',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'User Agent',
        desc: 'Input user agent to be parsed',
      },
    },
    browser: {
      value: 'browser',
      label: 'UserAgentToBrowser',
      group: 'uaGroup',
      help: {
        desc: 'Extracts Browser from User Agent',
        ex: 'UserAgenttoBrowser("Mozilla/5.0 (iPhone; CPU iPhone OS 1351 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/13.1.1 Mobile/15E148 Safari/604.1") ➠ "Mobile Safari"',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'User Agent',
        desc: 'Input user agent to be parsed',
      },
    },
    browser_version: {
      value: 'browser_version',
      label: 'UserAgentToBrowserVersion',
      group: 'uaGroup',
      help: {
        desc: 'Extracts Browser Version from User Agent',
        ex: 'UserAgenttoBrowserVersion("Mozilla/5.0 (iPhone; CPU iPhone OS 1351 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/13.1.1 Mobile/15E148 Safari/604.1") ➠ "13.1.1"',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'User Agent',
        desc: 'Input user agent to be parsed',
      },
    },
    operating_system: {
      value: 'operating_system',
      label: 'UserAgentToOS',
      group: 'uaGroup',
      help: {
        desc: 'Extracts Operating System from User Agent',
        ex: 'UserAgenttoOS("Mozilla/5.0 (iPhone; CPU iPhone OS 1351 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/13.1.1 Mobile/15E148 Safari/604.1") ➠ "iOS"',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'User Agent',
        desc: 'Input user agent to be parsed',
      },
    },
    iso8601FromString: {
      value: 'iso8601FromString',
      label: 'TimeStringToISO8601',
      group: 'dtGroup',
      help: {
        desc: 'Converts a valid date-time string to the corresponding ISO8601 formatted string timestamp.',
        ex: 'TimeStringToISO8601("25/02/2017") ➠ "2017-02-25T00:00:00"',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'Time (String)',
        desc: 'Time (String) to convert into ISO8601 format. This should comply with some valid date-time format.',
      },
    },
    iso8601FromEpoch: {
      value: 'iso8601FromEpoch',
      label: 'TimeEpochToISO8601',
      group: 'dtGroup',
      help: {
        desc: 'Converts a valid unix epoch time to the corresponding ISO8601 formatted string timestamp.',
        ex: 'TimeEpochToISO8601(1604994265) ➠ "2020-11-10T07:44:25Z"',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'Time (Epoch)',
        desc: 'Time (Unix Epoch)  to convert into ISO8601 format.',
      },
    },
    convertTimeZone: {
      value: 'convertTimeZone',
      label: 'ConvertTimezone',
      group: 'dtGroup',
      help: {
        desc: 'Converts a valid datetime string from one timezone to another timezone. Returns an ISO8601 format date-time string.',
        ex: 'ConvertTimeZone("2020/11/09", "GMT", "America/NewYork") ➠ "2020-11-08T19:00:00-05:00"',
      },
      requiredParams: ['__srcAttr', 'fromTz', 'toTz'],
      __srcAttr: {
        display_name: 'Time string',
        desc: 'Date time string that needs to be converted to different timezone.',
      },
      params: [
        {
          name: 'fromTz',
          display_name: 'From Timezone',
          desc: 'Timezone to convert from.',
          typesAllowed: ['string'],
          inputElement: 'select',
          format: 'timezone',
          values: [],
        },
        {
          name: 'toTz',
          display_name: 'To Timezone',
          desc: 'Timezone to convert to.',
          typesAllowed: ['string'],
          inputElement: 'select',
          format: 'timezone',
          values: [],
        },
      ],
    },
    extractDate: {
      value: 'extractDate',
      label: 'ExtractDateTime',
      group: 'dtGroup',
      help: {
        desc: 'Parses a valid date time string and returns specific date-time parts from the input.',
        ex: 'ExtractDateTime("09/28/2017 11:43:00","dd-MMM-yy") ➠  "28-Sep-17"',
      },
      requiredParams: ['__srcAttr', 'componentPattern'],
      __srcAttr: {
        display_name: 'Input Date',
        desc: 'String value that needs to be parsed. Note that input attribute must be a string of valid date time format, example: 2018-09-23',
      },
      params: [
        {
          name: 'componentPattern',
          display_name: 'Convert to format',
          desc: 'Desired format of output that includes the extracted date-time parts of input string.',
          typesAllowed: ['string'],
          inputElement: 'select-creatable',
          values: [
            'MM/dd',
            'MM/dd/yyyy',
            'yy',
            'yyyy',
            'MM',
            'MMM',
            'MMMM',
            'hh',
            'hh:mm',
            'HH',
            'HH:mm',
            'DD',
          ],
        },
      ],
    },
    timestampDifference: {
      value: 'timestampDifference',
      label: 'TimestampDifference',
      group: 'dtGroup',
      help: {
        desc: 'Returns the absolute difference of two timestamps in one of the supported ISO8601 units of time. Note that this function returns the absolute difference between the two timestamps.',
        ex: 'TimestampDifference("2016-03-22T22:54:14Z","2016-03-22T22:44:14Z","m") ➠ 10',
      },
      requiredParams: ['__srcAttr', 'fromTs', 'diffUnit'],
      __srcAttr: {
        display_name: 'Timestamp 1',
        desc: 'First timestamp to be used for computing the difference of timestamps.',
      },
      params: [
        {
          name: 'fromTs',
          display_name: 'Timestamp 2',
          desc: 'Second timestamp to be used for computing the difference of timestamps.',
          typesAllowed: ['function', 'attributeRef', 'systemAttributeRef', 'string'],
          inputElement: 'text',
          format: 'string',
        },
        {
          name: 'diffUnit',
          display_name: 'Difference Unit',
          desc: 'Select the units for computing difference:\n\n            sss - milli seconds\n\n            S - seconds\n\n            M- Minutes\n\n            H - Hours\n\n            D - Days\n\n            W- Weeks\n',
          typesAllowed: ['string'],
          inputElement: 'select',
          values: ['sss', 'S', 'M', 'H', 'D', 'W'],
        },
      ],
    },
    vin2country: {
      value: 'vin2country',
      label: 'VINToCountry',
      group: 'automotiveGroup',
      help: {
        desc: 'Extracts the country name from a valid VIN string.',
        ex: 'VINtoCountry("1GNDM19X35B110457") ➠ "United States"',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'VIN',
        desc: 'Valid VIN string to be parsed.',
      },
    },
    vin2manufacturer: {
      value: 'vin2manufacturer',
      label: 'VINToManufacturer',
      group: 'automotiveGroup',
      help: {
        desc: 'Extracts the vehicle manufacturer from a valid VIN string.',
        ex: 'VINtoManufacturer("1GNDM19X35B110457") ➠ "Chevrolet"',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'VIN',
        desc: 'Valid VIN string to be parsed.',
      },
    },
    vin2year: {
      value: 'vin2year',
      label: 'VINToYear',
      group: 'automotiveGroup',
      help: {
        desc: 'Extracts the vehicle make year from a valid VIN string.',
        ex: 'VINtoYear("1GNDM19X35B110457") ➠ "2005"',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'VIN',
        desc: 'Valid VIN string to be parsed.',
      },
    },
    eqAttr: {
      value: 'eqAttr',
      label: 'EqualToAttribute',
      group: 'miscGroup',
      help: {
        desc: 'Pass through the selected attribute as is without any transformation.',
        ex: 'If attribute <name> has value "Test"\nEqualtoAttribute(<name>) ➠ "Test"',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'Equal to',
        desc: 'The attribute value that should be passed through without transformation.',
        typesAllowed: ['attributeRef', 'systemAttributeRef'],
      },
    },
    constVal: {
      value: 'constVal',
      label: 'Constant',
      ignoreSrc: true,
      group: 'miscGroup',
      help: {
        desc: 'Always returns a constant value (string or number).',
        ex: 'Constant("One") ➠ "One"',
      },
      requiredParams: ['val'],
      params: [
        {
          name: 'val',
          display_name: 'Value',
          desc: 'The constant value that should be returned as the output of this function.',
          typesAllowed: ['string', 'number', 'boolean'],
          inputElement: 'text',
          default: {
            string: '',
            number: 0,
            boolean: false,
          },
        },
      ],
    },
    remove: {
      value: 'remove',
      label: 'RemoveAttribute',
      ignoreDest: true,
      group: 'miscGroup',
      help: {
        desc: 'Remove the selected attribute from the output.',
        ex: '-',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'Remove',
        desc: 'The attribute that should be removed from the output.',
        typesAllowed: ['attributeRef', 'systemAttributeRef'],
      },
    },
    hoist: {
      value: 'hoist',
      label: 'HoistObjectProperties',
      ignoreDest: true,
      group: 'miscGroup',
      help: {
        desc: 'Extract all properties of the given object and add them to the root.',
        ex: 'HoistObjectProperties({o: {x: 2, y: 5}}) ➠ {x: 2, y: 5}',
      },
      requiredParams: ['__srcAttr'],
      __srcAttr: {
        display_name: 'Attribute',
        desc: 'The object containing the properties to be hoisted',
        typesAllowed: ['attributeRef', 'systemAttributeRef'],
      },
    },
    getNexsetData: {
      value: 'getNexsetData',
      label: 'GetNexsetData',
      group: 'miscGroup',
      help: {
        desc: "Fetch data from a Nexset by calling the Data Feed API of the Nexset. You can also specify Data Feed API payload if the Nexset's Data Feed API call accepts optional payload. See https://www.nexla.com/data-api/ for more information about Nexset Data APIs",
        ex: 'getNexsetData(1001, "abcdefg", {state: "California"}) ➠ {"zip": 94001, "st": "CA", "city":"San Mateo"}',
      },
      requiredParams: ['dataSetId', 'apiKey', 'selectors'],
      ignoreSrc: true,
      params: [
        {
          name: 'dataSetId',
          display_name: 'Nexset ID',
          desc: 'The Nexset ID you wish to fetch data from.',
          typesAllowed: ['number', 'attributeRef', 'systemAttributeRef'],
          inputElement: 'text',
          format: 'number',
        },
        {
          name: 'apiKey',
          display_name: 'API Key',
          desc: 'Data Feed API key of the Nexset whose data you wish to fetch.',
          typesAllowed: ['function', 'attributeRef', 'systemAttributeRef', 'string'],
          inputElement: 'text',
          format: 'string',
          default: {
            string: '',
          },
        },
        {
          name: 'selectors',
          display_name: 'Data Feed API Payload',
          desc: 'Set the payload that should be used when executing the Data Feed API call. This is an optional property and required only if the Data Feed API requires any payload.',
          typesAllowed: ['attributeRef', 'function', 'systemAttributeRef', 'string'],
          inputElement: 'object',
          format: 'string',
          default: {
            string: '',
          },
        },
      ],
    },
  },
};
